import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  Popover,
  SwipeableDrawer,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteRounded from "@mui/icons-material/DeleteRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import LogoutRounded from "@mui/icons-material/LogoutRounded";
import MailRounded from "@mui/icons-material/MailRounded";
import QrCode2Rounded from "@mui/icons-material/QrCode2Rounded";
import ShieldRounded from "@mui/icons-material/ShieldRounded";
import VpnKeyRounded from "@mui/icons-material/VpnKeyRounded";
import WhatsApp from "@mui/icons-material/WhatsApp";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import { COLORS } from "../assets/theme";
import { NAV, resetRow } from "./layout/sidebar/navTheme";
import ToastNotification from "./ToastNotification";
import {
  fetchUser2faSettings,
  updateUser2faSettings,
  provisionUserTotp,
  disableUserTotpProvision,
  TotpProvisionResponse,
  User2faSettings,
} from "../services/UsersService";

interface DecodedToken {
  given_name: string;
  email?: string;
  preferred_username?: string;
  realm_access?: {
    roles: string[];
  };
}

function extractIssuerFromUri(uri: string): string | null {
  try {
    const url = new URL(uri);
    const issuerParam = url.searchParams.get("issuer");
    if (issuerParam) return issuerParam;

    // Fallback: parse from path
    const path = url.pathname.substring(1); // Remove leading '/'
    const colonIndex = path.indexOf(":");
    if (colonIndex > 0) {
      return decodeURIComponent(path.substring(0, colonIndex));
    }

    return null;
  } catch {
    return null;
  }
}

/** Gold-on-dark switch matching the nav accent. */
const switchSx = {
  "& .MuiSwitch-switchBase": { color: "rgba(255,255,255,0.75)" },
  "& .MuiSwitch-switchBase.Mui-checked": { color: NAV.accent },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: NAV.accent,
    opacity: 0.5,
  },
  "& .MuiSwitch-track": {
    backgroundColor: "rgba(255,255,255,0.28)",
    opacity: 1,
  },
};

interface ToggleRowProps {
  icon: React.ReactNode;
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}

const ToggleRow: React.FC<ToggleRowProps> = ({
  icon,
  label,
  hint,
  checked,
  onChange,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      minHeight: 52,
      px: 1.5,
      borderRadius: "12px",
      transition: `background-color 160ms ${NAV.ease}`,
      "@media (hover: hover)": { "&:hover": { backgroundColor: NAV.hover } },
    }}
  >
    <Box
      sx={{
        width: 34,
        height: 34,
        flexShrink: 0,
        borderRadius: "10px",
        display: "grid",
        placeItems: "center",
        color: checked ? NAV.accent : NAV.textMuted,
        backgroundColor: checked ? NAV.accentSoft : NAV.hover,
        transition: `background-color 160ms ${NAV.ease}, color 160ms ${NAV.ease}`,
      }}
    >
      {icon}
    </Box>

    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: "0.875rem",
          fontWeight: 550,
          color: NAV.textActive,
          lineHeight: 1.3,
        }}
      >
        {label}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: "0.6875rem", color: NAV.textMuted }}>
          {hint}
        </Typography>
      )}
    </Box>

    <Switch
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      inputProps={{ "aria-label": label }}
      sx={switchSx}
    />
  </Box>
);

const UserInfo = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const accessToken = localStorage.getItem("access_token");
  let name = "";
  let role = "";
  let userEmail = "";

  const allowedRoles = [
    "RATING_TEAM",
    "SUPER",
    "BILLING_TEAM",
    "SUPPORT_TEAM",
    "ROUTING_TEAM",
    "SALES_TEAM",
  ];
  const notAllowedRoles = [
    "COMPANIES",
    "PROVIDER",
    "CUSTOMER",
    "INTELLIGENT_ROUTE",
    "BILLING_MANAGEMENT",
    "NOTIFICATION",
    "MESSAGE_ACTIVITY",
    "CONFIGURATION",
    "default-roles-ezz-gateway",
    "offline_access",
    "uma_authorization",
  ];

  if (accessToken) {
    try {
      const decoded = jwtDecode<DecodedToken>(accessToken);
      name = decoded.given_name;
      userEmail = decoded.email || decoded.preferred_username || "";

      const userRoles = decoded.realm_access?.roles || [];

      role = userRoles.find((r) => allowedRoles.includes(r)) || "";

      if (!role) {
        role = userRoles.find((r) => !notAllowedRoles.includes(r)) || "";
      }
    } catch (error) {
      console.error("Failed to decode token", error);
    }
  }

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Separate from `anchorEl`: the mobile sheet has no anchor.
  const [open, setOpen] = useState(false);
  const [user2faSettings, setUser2faSettings] =
    useState<User2faSettings | null>(null);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [twoFactorEmailEnabled, setTwoFactorEmailEnabled] = useState(false);
  const [twoFactorWhatsappEnabled, setTwoFactorWhatsappEnabled] =
    useState(false);
  const [twoFactorTotpEnabled, setTwoFactorTotpEnabled] = useState(false);
  const [totpProvisionedAt, setTotpProvisionedAt] = useState<string>("");
  const [totpProvision, setTotpProvision] =
    useState<TotpProvisionResponse | null>(null);
  const [showTotpQr, setShowTotpQr] = useState(false);
  const [isProvisioningTotp, setIsProvisioningTotp] = useState(false);
  const [isDisablingTotpProvision, setIsDisablingTotpProvision] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // A press landing just after a backdrop dismiss would otherwise re-open.
  const dismissedAtRef = useRef(0);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (open) {
      requestClose();
      return;
    }
    if (Date.now() - dismissedAtRef.current < 250) return;
    setAnchorEl(event.currentTarget);
    setOpen(true);
    loadUserData();
  };

  /** The server-side values the panel was last synced with. */
  const baseline = {
    email: user2faSettings?.twoFactorEmailEnabled ?? false,
    whatsapp: user2faSettings?.twoFactorWhatsappEnabled ?? false,
    totp: user2faSettings?.twoFactorTotpEnabled ?? false,
  };

  const isDirty =
    Boolean(user2faSettings) &&
    (twoFactorEmailEnabled !== baseline.email ||
      twoFactorWhatsappEnabled !== baseline.whatsapp ||
      twoFactorTotpEnabled !== baseline.totp);

  /** Never writes — callers deal with unsaved edits. */
  const closeNow = () => {
    setConfirmDiscardOpen(false);
    setOpen(false);
    setAnchorEl(null);
    dismissedAtRef.current = Date.now();
  };

  const requestClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      dismissedAtRef.current = Date.now();
      return;
    }
    closeNow();
  };

  const revertToBaseline = () => {
    setTwoFactorEmailEnabled(baseline.email);
    setTwoFactorWhatsappEnabled(baseline.whatsapp);
    setTwoFactorTotpEnabled(baseline.totp);
  };

  const saveSettings = async (): Promise<boolean> => {
    if (!userEmail || !user2faSettings) return false;
    try {
      setIsSaving(true);
      await updateUser2faSettings(userEmail, {
        twoFactorEmailEnabled,
        twoFactorTotpEnabled,
        twoFactorWhatsappEnabled,
      });
      // Re-baseline locally so the panel goes clean without a refetch.
      setUser2faSettings({
        ...user2faSettings,
        twoFactorEmailEnabled,
        twoFactorTotpEnabled,
        twoFactorWhatsappEnabled,
      });
      ToastNotification.success("Two-factor settings updated successfully");
      return true;
    } catch (error: any) {
      console.error("Error updating 2FA settings:", error);
      ToastNotification.error(
        error?.message || "Failed to update two-factor settings",
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndClose = async () => {
    const saved = await saveSettings();
    // Keep the panel open on failure so edits aren't dropped.
    if (saved) closeNow();
    else setConfirmDiscardOpen(false);
  };

  const handleDiscardAndClose = () => {
    revertToBaseline();
    closeNow();
  };

  useEffect(() => {
    if (open && userEmail && !user2faSettings) {
      loadUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userEmail]);

  const loadUserData = async () => {
    if (!userEmail) return;
    try {
      setLoadingUserData(true);
      const data = await fetchUser2faSettings(userEmail);
      setUser2faSettings(data);
      setTwoFactorEmailEnabled(data.twoFactorEmailEnabled ?? false);
      setTwoFactorWhatsappEnabled(data.twoFactorWhatsappEnabled ?? false);
      setTwoFactorTotpEnabled(data.twoFactorTotpEnabled ?? false);
      setTotpProvisionedAt(data.totpProvisionedAt || "");
    } catch (error: any) {
      console.error("Error loading user data:", error);
      ToastNotification.error("Failed to load user data");
    } finally {
      setLoadingUserData(false);
    }
  };

  const handleProvisionTotp = async () => {
    if (!userEmail) return;
    try {
      setIsProvisioningTotp(true);
      const res = await provisionUserTotp(userEmail);
      setTotpProvision(res);
      setShowTotpQr(true);
      setTwoFactorTotpEnabled(true);
      setTotpProvisionedAt(res.provisionedAt);
      ToastNotification.success(
        "Key created successfully. Scan the QR code to set up Authenticator.",
      );
    } catch (error: any) {
      console.error("Error creating key:", error);
      ToastNotification.error(error?.message || "Failed to create key.");
    } finally {
      setIsProvisioningTotp(false);
    }
  };

  const handleDisableTotpProvision = async () => {
    if (!userEmail) return;
    try {
      setIsDisablingTotpProvision(true);
      const updated = await disableUserTotpProvision(userEmail);
      setTotpProvisionedAt(updated.totpProvisionedAt || "");
      setTotpProvision(null);
      setShowTotpQr(false);
      ToastNotification.success("Key deleted successfully.");
      await loadUserData();
    } catch (error: any) {
      console.error("Error deleting key:", error);
      ToastNotification.error(error?.message || "Failed to delete key.");
    } finally {
      setIsDisablingTotpProvision(false);
    }
  };

  function getInitials(fullName: string): string {
    const parts = fullName.trim().split(" ");
    if (parts.length > 1) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  }

  const initials = name ? getInitials(name) : "";

  const provisioningUri =
    user2faSettings?.totpProvisioningUri ||
    totpProvision?.provisioningUri ||
    "";
  const hasProvisioningUri = Boolean(provisioningUri);

  const iconActionSx = (danger?: boolean) => ({
    ...resetRow,
    width: 38,
    height: 38,
    borderRadius: "11px",
    display: "grid",
    placeItems: "center",
    color: danger ? COLORS.RED : NAV.accent,
    backgroundColor: NAV.hover,
    transition: `background-color 160ms ${NAV.ease}, opacity 160ms ${NAV.ease}`,
    "@media (hover: hover)": {
      "&:hover:not(:disabled)": { backgroundColor: NAV.pressed },
    },
    "&:disabled": { color: NAV.textMuted, cursor: "default", opacity: 0.55 },
  });

  const panelContent = (
    <>
      {/* Identity header ------------------------------------------------- */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 2,
          borderBottom: `1px solid ${NAV.border}`,
        }}
      >
        <Avatar
          sx={{
            width: 44,
            height: 44,
            bgcolor: NAV.accentSoft,
            border: `1px solid ${NAV.accentBorder}`,
            color: NAV.accent,
            fontWeight: 700,
            fontSize: "0.9375rem",
          }}
        >
          {initials}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            noWrap
            sx={{
              fontSize: "0.9375rem",
              fontWeight: 650,
              color: NAV.textActive,
            }}
          >
            {name}
          </Typography>
          <Typography noWrap sx={{ fontSize: "0.75rem", color: NAV.textMuted }}>
            {userEmail}
          </Typography>
        </Box>
        {role && (
          <Box
            sx={{
              flexShrink: 0,
              alignSelf: "flex-start",
              fontSize: "0.625rem",
              fontWeight: 700,
              letterSpacing: "0.06em",
              color: NAV.accent,
              backgroundColor: NAV.accentSoft,
              border: `1px solid ${NAV.accentBorder}`,
              borderRadius: "999px",
              px: 1,
              py: 0.25,
            }}
          >
            {role}
          </Box>
        )}
      </Box>

      {/* Two-factor authentication --------------------------------------- */}
      <Box sx={{ px: 1.5, pt: 2, pb: 1 }}>
        <Typography
          sx={{
            px: 0.5,
            pb: 1,
            fontSize: "0.6875rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: NAV.textMuted,
          }}
        >
          Two-Factor Authentication
        </Typography>

        {loadingUserData ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
            <CircularProgress size={26} sx={{ color: NAV.accent }} />
          </Box>
        ) : (
          <Box
            sx={{
              borderRadius: "14px",
              backgroundColor: NAV.surfaceSunken,
              border: `1px solid ${NAV.border}`,
              p: 0.5,
            }}
          >
            <ToggleRow
              icon={<MailRounded sx={{ fontSize: 19 }} />}
              label="Email"
              checked={twoFactorEmailEnabled}
              onChange={setTwoFactorEmailEnabled}
            />
            <ToggleRow
              icon={<WhatsApp sx={{ fontSize: 19 }} />}
              label="WhatsApp"
              checked={twoFactorWhatsappEnabled}
              onChange={setTwoFactorWhatsappEnabled}
            />
            <ToggleRow
              icon={<ShieldRounded sx={{ fontSize: 19 }} />}
              label="Authenticator"
              hint={totpProvisionedAt ? "Key active" : "No key yet"}
              checked={twoFactorTotpEnabled}
              onChange={setTwoFactorTotpEnabled}
            />

            {/* Key actions get their own row so the 38px targets never collide
                with the switch above them on a narrow screen. */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 0.75,
                px: 1.5,
                pb: 1,
                pt: 0.5,
              }}
            >
              <Tooltip title="Show QR code" arrow>
                <Box component="span">
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setShowTotpQr(!showTotpQr)}
                    disabled={!hasProvisioningUri}
                    aria-label="Show QR code"
                    sx={iconActionSx()}
                  >
                    <QrCode2Rounded sx={{ fontSize: 19 }} />
                  </Box>
                </Box>
              </Tooltip>

              <Tooltip title="Create new key" arrow>
                <Box component="span">
                  <Box
                    component="button"
                    type="button"
                    onClick={handleProvisionTotp}
                    disabled={isProvisioningTotp || !userEmail}
                    aria-label="Create new key"
                    sx={iconActionSx()}
                  >
                    <VpnKeyRounded sx={{ fontSize: 19 }} />
                  </Box>
                </Box>
              </Tooltip>

              <Tooltip title="Delete key" arrow>
                <Box component="span">
                  <Box
                    component="button"
                    type="button"
                    onClick={handleDisableTotpProvision}
                    disabled={
                      isDisablingTotpProvision ||
                      !userEmail ||
                      !totpProvisionedAt
                    }
                    aria-label="Delete key"
                    sx={iconActionSx(true)}
                  >
                    <DeleteRounded sx={{ fontSize: 19 }} />
                  </Box>
                </Box>
              </Tooltip>
            </Box>

            {showTotpQr && hasProvisioningUri && (
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={NAV.spring}
                sx={{
                  mx: 1,
                  mb: 1,
                  pt: 2,
                  pb: 2,
                  borderTop: `1px solid ${NAV.border}`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    color: NAV.textActive,
                  }}
                >
                  Scan QR Code
                </Typography>
                {/* QR needs a light quiet zone to stay scannable on dark. */}
                <Box
                  sx={{
                    background: COLORS.White,
                    padding: 2,
                    borderRadius: "12px",
                  }}
                >
                  <QRCode value={provisioningUri} size={168} />
                </Box>
                {(() => {
                  const issuer =
                    totpProvision?.issuer ||
                    extractIssuerFromUri(provisioningUri);
                  return issuer ? (
                    <Typography
                      sx={{
                        fontSize: "0.6875rem",
                        color: NAV.textMuted,
                        textAlign: "center",
                      }}
                    >
                      Issuer: {issuer}
                    </Typography>
                  ) : null;
                })()}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Save bar — only present while there is something to save. --------- */}
      <AnimatePresence initial={false}>
        {isDirty && (
          <motion.div
            key="save-bar"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={NAV.springSoft}
            style={{ overflow: "hidden" }}
          >
            <Box sx={{ display: "flex", gap: 1, px: 1.5, pt: 0.5, pb: 1 }}>
              <Box
                component="button"
                type="button"
                onClick={revertToBaseline}
                disabled={isSaving}
                sx={{
                  ...resetRow,
                  flex: "0 0 auto",
                  minHeight: 44,
                  px: 2,
                  borderRadius: "12px",
                  color: NAV.textIdle,
                  border: `1px solid ${NAV.border}`,
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  "@media (hover: hover)": {
                    "&:hover:not(:disabled)": {
                      backgroundColor: NAV.hover,
                      color: NAV.textActive,
                    },
                  },
                  "&:disabled": { opacity: 0.5, cursor: "default" },
                }}
              >
                Reset
              </Box>
              <Box
                component={motion.button}
                type="button"
                whileTap={{ scale: 0.98 }}
                transition={NAV.spring}
                onClick={saveSettings}
                disabled={isSaving}
                sx={{
                  ...resetRow,
                  flex: 1,
                  minHeight: 44,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  borderRadius: "12px",
                  backgroundColor: NAV.accent,
                  color: COLORS.BlackBackground,
                  fontSize: "0.8125rem",
                  fontWeight: 700,
                  boxShadow: NAV.accentGlow,
                  "@media (hover: hover)": {
                    "&:hover:not(:disabled)": { filter: "brightness(1.08)" },
                  },
                  "&:disabled": { opacity: 0.7, cursor: "default" },
                }}
              >
                {isSaving && (
                  <CircularProgress
                    size={15}
                    sx={{ color: COLORS.BlackBackground }}
                  />
                )}
                {isSaving ? "Saving…" : "Save changes"}
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Log out ---------------------------------------------------------- */}
      <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
        <Box
          component={motion.button}
          type="button"
          whileTap={{ scale: 0.985 }}
          transition={NAV.spring}
          onClick={() => {
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            navigate("/login");
          }}
          sx={{
            ...resetRow,
            width: "100%",
            minHeight: 48,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 1.5,
            borderRadius: "12px",
            color: COLORS.RED,
            backgroundColor: "transparent",
            transition: `background-color 160ms ${NAV.ease}`,
            "@media (hover: hover)": {
              "&:hover": { backgroundColor: "rgba(255, 77, 79, 0.12)" },
            },
            "&:active": { backgroundColor: "rgba(255, 77, 79, 0.18)" },
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              flexShrink: 0,
              borderRadius: "10px",
              display: "grid",
              placeItems: "center",
              backgroundColor: "rgba(255, 77, 79, 0.14)",
            }}
          >
            <LogoutRounded sx={{ fontSize: 19 }} />
          </Box>
          <Typography
            sx={{ fontSize: "0.875rem", fontWeight: 600, color: "inherit" }}
          >
            Log out
          </Typography>
        </Box>
      </Box>
    </>
  );

  return (
    <>
      <Box
        component={motion.button}
        type="button"
        whileTap={{ scale: 0.96 }}
        transition={NAV.spring}
        onClick={handleClick}
        aria-label="Account menu"
        aria-haspopup="true"
        aria-expanded={open}
        sx={{
          ...resetRow,
          display: "flex",
          alignItems: "center",
          gap: 1,
          height: 44,
          pl: 0.5,
          pr: { xs: 0.5, sm: 1 },
          borderRadius: "12px",
          transition: `background-color 160ms ${NAV.ease}`,
          "@media (hover: hover)": {
            "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
          },
        }}
      >
        <Avatar
          sx={{
            width: 34,
            height: 34,
            bgcolor: COLORS.BlackBackground,
            border: `2px solid ${COLORS.GOLD}`,
            fontWeight: "bold",
            color: COLORS.GOLD,
            fontSize: "0.75rem",
          }}
        >
          {initials}
        </Avatar>

        <Box
          sx={{
            display: { xs: "none", sm: "block" },
            minWidth: 0,
            textAlign: "left",
          }}
        >
          <Typography
            noWrap
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: COLORS.typographyColor,
              lineHeight: 1.25,
              maxWidth: 160,
            }}
          >
            {name}
          </Typography>
          {role && (
            <Typography
              noWrap
              sx={{
                fontSize: "0.6875rem",
                color: COLORS.placeHolderColor,
                lineHeight: 1.2,
                maxWidth: 160,
              }}
            >
              {role}
            </Typography>
          )}
        </Box>

        <ExpandMoreRounded
          sx={{
            fontSize: 18,
            color: COLORS.placeHolderColor,
            flexShrink: 0,
            transition: `transform 200ms ${NAV.ease}`,
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </Box>

      {isSmallScreen ? (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onOpen={() => setOpen(true)}
          onClose={requestClose}
          // The sheet has an explicit trigger; edge-swipe would open it by accident.
          disableSwipeToOpen
          PaperProps={{
            sx: {
              width: "100%",
              maxHeight: "88vh",
              backgroundColor: NAV.surfaceRaised,
              backgroundImage: "none",
              borderTopLeftRadius: "20px",
              borderTopRightRadius: "20px",
              borderTop: `1px solid ${NAV.borderStrong}`,
              // Clears the iOS home indicator.
              pb: "max(8px, env(safe-area-inset-bottom))",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
          }}
        >
          {/* Grab handle — always a valid drag target, since it sits outside
              the scroll container below it. */}
          <Box
            sx={{
              flex: "0 0 auto",
              display: "grid",
              placeItems: "center",
              pt: 1.25,
              pb: 1,
              cursor: "grab",
              touchAction: "none",
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 4,
                borderRadius: "999px",
                backgroundColor: NAV.borderStrong,
              }}
            />
          </Box>
          <Box sx={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>
            {panelContent}
          </Box>
        </SwipeableDrawer>
      ) : (
        <Popover
          anchorEl={anchorEl}
          open={open}
          onClose={requestClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                width: 380,
                maxHeight: "80vh",
                overflowY: "auto",
                borderRadius: "18px",
                backgroundColor: NAV.surfaceRaised,
                backgroundImage: "none",
                border: `1px solid ${NAV.borderStrong}`,
                boxShadow: NAV.flyoutShadow,
              },
            },
          }}
        >
          {panelContent}
        </Popover>
      )}

      {/* Unsaved-changes guard ------------------------------------------- */}
      <Dialog
        open={confirmDiscardOpen}
        onClose={() => setConfirmDiscardOpen(false)}
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(10, 10, 10, 0.55)" } },
          paper: {
            sx: {
              m: 2,
              width: "100%",
              maxWidth: 360,
              p: 2.5,
              borderRadius: "18px",
              backgroundColor: NAV.surfaceRaised,
              backgroundImage: "none",
              border: `1px solid ${NAV.borderStrong}`,
              boxShadow: NAV.flyoutShadow,
            },
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "1rem",
            fontWeight: 650,
            color: NAV.textActive,
            mb: 0.75,
          }}
        >
          Unsaved changes
        </Typography>
        <Typography
          sx={{
            fontSize: "0.8125rem",
            color: NAV.textIdle,
            mb: 2.5,
            lineHeight: 1.5,
          }}
        >
          Your two-factor settings have changed but haven't been saved yet.
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box
            component="button"
            type="button"
            onClick={handleDiscardAndClose}
            disabled={isSaving}
            sx={{
              ...resetRow,
              flex: 1,
              minHeight: 44,
              display: "grid",
              placeItems: "center",
              borderRadius: "12px",
              color: NAV.textIdle,
              border: `1px solid ${NAV.border}`,
              fontSize: "0.8125rem",
              fontWeight: 600,
              "@media (hover: hover)": {
                "&:hover:not(:disabled)": {
                  backgroundColor: NAV.hover,
                  color: NAV.textActive,
                },
              },
              "&:disabled": { opacity: 0.5, cursor: "default" },
            }}
          >
            Discard
          </Box>
          <Box
            component={motion.button}
            type="button"
            whileTap={{ scale: 0.98 }}
            transition={NAV.spring}
            onClick={handleSaveAndClose}
            disabled={isSaving}
            sx={{
              ...resetRow,
              flex: 1,
              minHeight: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              borderRadius: "12px",
              backgroundColor: NAV.accent,
              color: COLORS.BlackBackground,
              fontSize: "0.8125rem",
              fontWeight: 700,
              "@media (hover: hover)": {
                "&:hover:not(:disabled)": { filter: "brightness(1.08)" },
              },
              "&:disabled": { opacity: 0.7, cursor: "default" },
            }}
          >
            {isSaving && (
              <CircularProgress
                size={15}
                sx={{ color: COLORS.BlackBackground }}
              />
            )}
            {isSaving ? "Saving…" : "Save & close"}
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default UserInfo;
